import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {combineLatest, Subject} from 'rxjs';
import {debounceTime, startWith, takeUntil} from 'rxjs/operators';
import {ECCosts, ECCostType, ECEconomicsAggregatedResponse} from 'smag-commons/dto/eco';
import {isDefined} from '../../../../commons/commons';
import {validateNotBlank} from '../../../../commons/validators';
import {ECCost} from 'smag-commons/dto/eco/community';
import {CACV_STRING, limitDecimals} from '../../../../shared/ec-economics';

interface CustomCosts {
    costName: string;
    computedPercentage: number;
    computedPod: number;
    computedSharedEnergy: number;
    costPercentage: number;
    costPod: number;
    costFixed: number;
    costSharedEnergy: number;
    costType: ECCostType;
    lowerBound?: number;
    costPercentageLowerbound: number;
}

interface FormCosts {
    costs: CustomCosts[];
    useCacv: boolean;
}

const EMPTY_COST: CustomCosts = {
    costName: '',
    computedPercentage: 0,
    computedPod: 0,
    computedSharedEnergy: 0,
    costPercentage: 0,
    costPod: 0,
    costFixed: 0,
    costSharedEnergy: 0,
    costType: ECCostType.SHARED_ENERGY,
    lowerBound: null,
    costPercentageLowerbound: 0,
};

@Component({
    selector: 'app-ec-costs',
    templateUrl: './ec-costs.component.html',
    styleUrls: ['./ec-costs.component.scss']
})
export class ECCostsComponent implements OnInit, OnDestroy {
    private destroyed = new Subject<void>();
    private readonly MAX_COST = 100000;
    @Input() response: ECEconomicsAggregatedResponse;
    @Input() totalPods: number;
    @Output() values: EventEmitter<ECCosts> = new EventEmitter<ECCosts>();
    @Output() valid: EventEmitter<boolean> = new EventEmitter<boolean>();
    form: FormGroup;

    readonly limitDecimals = limitDecimals;
    readonly ECCostType = ECCostType;
    readonly EMPTY_COST = EMPTY_COST;
    readonly CACV_STRING = CACV_STRING;

    constructor(private readonly fb: FormBuilder) {
        this.form = fb.group({
            useCacv: new FormControl(false, []),
            costs: new FormArray([])
        });
    }

    get costsFormArray(): FormArray {
        return this.form.controls.costs as FormArray;
    }

    get costsControls(): FormGroup[] {
        return (this.costsFormArray).controls as FormGroup[];
    }

    get useCacv(): boolean {
        return this.form.controls.useCacv.value;
    }

    get getTipValue() {
        return this.computeTotal(this.useCacv);
    }

    get computeTotalCosts() {
        let total = 0;
        this.costsControls.forEach((fg) => {
            const currentType = this.costType(fg);
            switch (currentType) {
                case ECCostType.SHARED_ENERGY:
                    total += this.computedSharedEnergy(fg).value;
                    break;
                case ECCostType.POD:
                    total += this.computedPod(fg).value;
                    break;
                case ECCostType.PERCENTAGE:
                    total += this.computedPercentage(fg).value;
                    break;
                case ECCostType.PERCENTAGE_LOWERBOUND:
                    total += this.computedPercentageLowerbound(fg).value;
                    break;
                case ECCostType.FIXED:
                    total += this.computedFixed(fg).value;
                    break;
                default:
                    return 0;
            }
        });
        return total;
    }

    ngOnInit() {
        if (this.response && this.response.costs.costs && this.response.costs.costs.costs.length > 0) {
            this.form.controls.useCacv.setValue(this.response.costs.costs.use_c_acv);
            this.response.costs.costs.costs.forEach((c => {
                const customCosts: CustomCosts = {
                    costName: c.name,
                    computedPercentage: 0,
                    computedPod: 0,
                    computedSharedEnergy: 0,
                    costPercentage: (c.type === ECCostType.PERCENTAGE) ? +(c.cost * 100).toFixed(2) : 0,
                    costPod: c.type === ECCostType.POD ? c.cost : 0,
                    costSharedEnergy: c.type === ECCostType.SHARED_ENERGY ? c.cost : 0,
                    costFixed: c.type === ECCostType.FIXED ? c.cost : 0,
                    costType: c.type,
                    lowerBound: c.type === ECCostType.PERCENTAGE_LOWERBOUND ? c.lower_bound : null,
                    costPercentageLowerbound: (c.type === ECCostType.PERCENTAGE_LOWERBOUND) ? +(c.cost * 100).toFixed(2) : 0,
                };
                this.addCostForm(customCosts);
            }));
            this.emitValues(this.form.value);
            this.valid.emit(this.form.valid);
        } else {
            this.addCostForm(EMPTY_COST);
        }
        this.form.valueChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(value => this.emitValues(value));
        this.form.statusChanges
            .pipe(takeUntil(this.destroyed))
            .subscribe(() => this.valid.emit(this.form.valid && this.costsControls.length > 0));
    }

    computedSharedEnergy(form: FormGroup) {
        return form.controls.computedSharedEnergy as AbstractControl;
    }

    computedPod(form: FormGroup) {
        return form.controls.computedPod as AbstractControl;
    }

    computedPercentage(form: FormGroup) {
        return form.controls.computedPercentage as AbstractControl;
    }

    computedPercentageLowerbound(form: FormGroup) {
        return form.controls.computedPercentageLowerBound as AbstractControl;
    }

    lowerBound(form: FormGroup): number {
        return isDefined(form.controls.lowerBound) ? form.controls.lowerBound.value : 0;
    }

    computedFixed(form: FormGroup) {
        return form.controls.computedFixed as AbstractControl;
    }

    addCostForm(customCosts: CustomCosts) {
        const costForm: FormGroup = this.fb.group({
            costName: new FormControl(customCosts.costName, [validateNotBlank]),
            costType: new FormControl(customCosts.costType, []),
            costSharedEnergy: new FormControl(customCosts.costSharedEnergy, [Validators.min(0), Validators.max(100000), Validators.maxLength(6)]),
            costPod: new FormControl(customCosts.costPod, [Validators.min(0), Validators.max(100000)]),
            costFixed: new FormControl(customCosts.costFixed, [Validators.min(0), Validators.max(100000)]),
            costPercentage: new FormControl(customCosts.costPercentage, [Validators.min(0), Validators.max(100)]),
            computedSharedEnergy: new FormControl(this.getTotalSharedEnergy(customCosts.costSharedEnergy), []),
            computedPod: new FormControl(this.getTotalPOD(customCosts.costPod), []),
            computedPercentage: new FormControl(this.getTotalPercentage(customCosts.costPercentage, 0,
                this.form.controls.useCacv.value), []),
            computedFixed: new FormControl(customCosts.costFixed, []),
            lowerBound: new FormControl(isDefined(customCosts.lowerBound) ? customCosts.lowerBound : null, [Validators.min(0), Validators.max(100000)]),
            computedPercentageLowerBound: new FormControl(this.getTotalPercentage(customCosts.costPercentageLowerbound, isDefined(customCosts.lowerBound) ? customCosts.lowerBound : 0,
                this.form.controls.useCacv.value), []),
            costPercentageLowerbound: new FormControl(customCosts.costPercentageLowerbound, [Validators.min(0), Validators.max(100)])
        });
        combineLatest([costForm.controls.costPercentageLowerbound.valueChanges
            .pipe(startWith(costForm.controls.costPercentageLowerbound.value)),
            this.form.controls.useCacv.valueChanges.pipe(startWith(this.useCacv))]).pipe(
            takeUntil(this.destroyed),
            debounceTime(500)
        ).subscribe(([value, useCacv]) => {
            if (value) {
                const lowerBound = isDefined(this.lowerBound(costForm)) ? this.lowerBound(costForm) : 0;
                const total = this.getTotalPercentage(+value, lowerBound, useCacv);
                this.computedPercentageLowerbound(costForm).setValue(total);
            } else {
                this.computedPercentageLowerbound(costForm).setValue(0);
            }
        });
        combineLatest([costForm.controls.costPercentage.valueChanges
            .pipe(startWith(costForm.controls.costPercentage.value)),
            this.form.controls.useCacv.valueChanges.pipe(startWith(this.useCacv))]).pipe(
            takeUntil(this.destroyed),
            debounceTime(500)
        ).subscribe(([value, useCacv]) => {
            if (value) {
                const total = this.getTotalPercentage(+value, 0, useCacv);
                this.computedPercentage(costForm).setValue(total);
            } else {
                this.computedPercentage(costForm).setValue(0);
            }
        });
        combineLatest([costForm.controls.lowerBound.valueChanges
            .pipe(startWith(costForm.controls.lowerBound.value)),
            this.form.controls.useCacv.valueChanges.pipe(startWith(this.useCacv))])
            .pipe(
                takeUntil(this.destroyed),
                debounceTime(500)
            ).subscribe(([value, useCacv]) => {
            if (value) {
                const total = this.getTotalPercentage(costForm.controls.costPercentageLowerbound.value, value, useCacv);
                this.computedPercentageLowerbound(costForm).setValue(total);
            } else {
                const total = this.getTotalPercentage(costForm.controls.costPercentageLowerbound.value, 0, useCacv);
                this.computedPercentageLowerbound(costForm).setValue(total);
            }
        });
        costForm.controls.costSharedEnergy.valueChanges.pipe(
            takeUntil(this.destroyed),
            debounceTime(250)
        ).subscribe(value => {
            if (value > this.MAX_COST) {
                costForm.controls.costSharedEnergy.setValue(this.MAX_COST);
            }
            if (value) {
                const total = this.getTotalSharedEnergy(+value);
                this.computedSharedEnergy(costForm).setValue(total);
            } else {
                this.computedSharedEnergy(costForm).setValue(0);
            }
        });
        costForm.controls.costPod.valueChanges.pipe(
            takeUntil(this.destroyed),
            debounceTime(250)
        ).subscribe(value => {
            if (value > this.MAX_COST) {
                costForm.controls.costPod.setValue(this.MAX_COST);
            }
            if (value && this.totalPods) {
                const total = this.getTotalPOD(+value);
                this.computedPod(costForm).setValue(total);
            } else {
                console.warn('Cannot retrieve number of total pods');
                this.computedPod(costForm).setValue(0);
            }
        });
        costForm.controls.costFixed.valueChanges.pipe(
            takeUntil(this.destroyed),
            debounceTime(250)
        ).subscribe(value => {
            if (value > this.MAX_COST) {
                costForm.controls.costFixed.setValue(this.MAX_COST);
            }
            if (value) {
                this.computedFixed(costForm).setValue(value);
            } else {
                console.warn('Cannot retrieve fixed value');
                this.computedFixed(costForm).setValue(0);
            }
        });
        this.costsFormArray.push(costForm);
    }

    isControlOK(form: FormGroup, controlName: string): boolean {
        return form.controls[controlName].dirty && !form.controls[controlName].valid;
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    onClickRemoveCost(i: number) {
        this.costsFormArray.removeAt(i);
    }

    computeTotal(useCacv: boolean) {
        const gseCosts = isDefined(this.response.costs.ec_gse_cost) ? this.response.costs.ec_gse_cost : 0;
        const tipEstimatedGseCost = isDefined(this.response.incentivation.estimated_gross_tip) &&
        isDefined(this.response.incentivation.estimated_gross_c_acv)
            ? (this.response.incentivation.estimated_gross_tip / (this.response.incentivation.estimated_gross_tip + this.response.incentivation.estimated_gross_c_acv)) * gseCosts :
            0;
        const tipGseCost = isDefined(this.response.incentivation.gross_tip) &&
        isDefined(this.response.incentivation.gross_c_acv)
            ? (this.response.incentivation.gross_tip / (this.response.incentivation.gross_tip + this.response.incentivation.gross_c_acv)) * gseCosts :
            0;
        if (this.response.gse) {
            return useCacv ? this.response.incentivation.gross_tip + this.response.incentivation.gross_c_acv : this.response.incentivation.gross_tip;
        } else {
            return useCacv ? this.response.incentivation.estimated_gross_tip && this.response.incentivation.estimated_gross_c_acv
                    ? this.response.incentivation.estimated_gross_tip + this.response.incentivation.estimated_gross_c_acv - gseCosts
                    : this.response.incentivation.gross_tip + this.response.incentivation.gross_c_acv - gseCosts
                : this.response.incentivation.estimated_gross_tip
                    ? this.response.incentivation.estimated_gross_tip - tipEstimatedGseCost
                    : this.response.incentivation.gross_tip - tipGseCost;
        }
    }

    costType(formGroup: FormGroup) {
        return formGroup.controls.costType.value as ECCostType;
    }

    isNotSelected(costForm: FormGroup, type: ECCostType) {
        const currentCostType = this.costType(costForm);
        return currentCostType !== type;
    }

    private emitValues(value) {
        const ecCosts: ECCosts = {
            use_c_acv: this.useCacv,
            costs: this.makeEcCosts(value)
        };
        this.values.emit(ecCosts);
    }

    private makeEcCosts(value: FormCosts) {
        return value.costs.map(c => {
            let costValue = 0;
            let lowerBound = null;
            switch (c.costType) {
                case ECCostType.SHARED_ENERGY:
                    costValue = c.costSharedEnergy;
                    break;
                case ECCostType.POD:
                    costValue = c.costPod;
                    break;
                case ECCostType.PERCENTAGE:
                    costValue = c.costPercentage / 100;
                    break;
                case ECCostType.PERCENTAGE_LOWERBOUND:
                    costValue = Number(c.costPercentageLowerbound) / 100;
                    lowerBound = c.lowerBound;
                    break;
                case ECCostType.FIXED:
                    costValue = c.costFixed;
                    break;
            }
            return {
                name: c.costName,
                cost: costValue,
                type: c.costType,
                lower_bound: lowerBound,
            } as ECCost;
        });
    }

    private getTotalPercentage(value: number, lowerBound: number, useCacv: boolean) {
        const total = this.computeTotal(useCacv);
        const percentage = (total * value) / 100;
        return percentage < lowerBound ? lowerBound : percentage;
    }

    private getTotalSharedEnergy(value: number) {
        if (value === 0) {
            return 0;
        }
        return (this.response.energy.shared_energy / 1000) * value;
    }

    private getTotalPOD(value: number) {
        return this.totalPods * value;
    }

}
